import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import Inspections from 'assets/inspections-tab-icon.svg?react'
import { US_NAVY_ORG_PERMISSION } from 'features/navbar/orgPermissions'
import { PARTS_DATABASE_PERMISSIONS_LIST } from 'features/partsDatabase'
import { ASSET_OVERVIEW_VIEW_PERMISSIONS } from 'features/permissions/utils/assetOverviewPermissions'
import { DOCUMENT_NAVIGATOR_VIEW_PERMISSIONS } from 'features/permissions/utils/documentNavigatorPermissions'
import {
  AlertTriangle,
  BarChart3,
  Bell,
  BellRing,
  Blocks,
  CalendarRange,
  ChartGantt,
  ChartLine,
  ChartPie,
  ClipboardList,
  Database,
  DollarSign,
  Factory,
  FileText,
  GitBranch,
  Heart,
  Home,
  Layers,
  LockKeyhole,
  Map,
  Network,
  Notebook,
  NotebookPen,
  PencilLine,
  Ship,
  UploadCloud,
  Workflow,
  Wrench,
} from 'lucide-react'
import React from 'react'
import {
  SEARCH_PARAM_ORG_ID,
  SEARCH_PARAM_SITE_ID,
} from 'routes/(global_search_params)/-searchParams'
import { createLibraSearch } from 'routes/libra/-searchParams'
import { createPointCloudIndexSearch } from 'routes/point-cloud/-searchParams'
import { createProcessDataOrgSiteSearch } from 'routes/process-data/-searchParams'
import { createProcessFlowsSearch } from 'routes/process-flows/-searchParams'
import { createProductionDashboardSearch } from 'routes/production-dashboard/-searchParams'
import { createShipsSearch } from 'routes/ships/-searchParams'
import { createVirtualReportsSearch } from 'routes/virtual-reports/-searchParams'
import { createWalkdownsSearch } from 'routes/walkdowns/-searchParams'
import type { FileRouteTypes } from 'routeTree.gen'
import styled, { css } from 'styled-components'
import {
  PERMISSION_CANTILEVER_ANOMALY_DETECTION_EDITOR,
  PERMISSION_CANTILEVER_BUDGETING_EDITOR,
  PERMISSION_CANTILEVER_BUDGETING_VIEWER,
  PERMISSION_CANTILEVER_CASES_EDITOR,
  PERMISSION_CANTILEVER_CASES_VIEWER,
  PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
  PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_ADMIN,
  PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_EDITOR,
  PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_VIEWER,
  PERMISSION_CANTILEVER_LOTO_VIEWER,
  PERMISSION_CANTILEVER_NAVY_SHIPS_VIEWER,
  PERMISSION_CANTILEVER_POWER_FIM_VIEWER,
  PERMISSION_CANTILEVER_PROCESS_FLOW_ADMIN,
  PERMISSION_CANTILEVER_SINGAPORE_MEMBER,
  PERMISSION_CANTILEVER_SYSTEM_VIEWER,
  PERMISSION_CANTILEVER_TIME_SERIES_EDITOR,
  PERMISSION_GECKO_INTERNAL_MEMBER,
  RolodexPermissionsValues,
} from 'types'

import { SelectedTab } from './types'

const FilledIcon = css`
  height: 1.25rem;
  width: 1.25rem;

  path {
    fill: currentColor;
  }
`

const OutlinedIcon = css`
  height: 1.25rem;
  width: 1.25rem;

  path {
    background-color: ${({ theme }) => theme.forecast.tertiaryText};
  }

  &:active {
    path {
      background-color: ${({ theme }) => theme.forecast.secondaryText};
    }
  }
`

const InspectionsIcon = styled(Inspections)`
  ${FilledIcon}
`

const HeartIcon = styled(Heart)`
  ${OutlinedIcon}
`

const HomeIcon = styled(Home)`
  ${OutlinedIcon}
`

const MapIcon = styled(Map)`
  ${OutlinedIcon}
`

const PencilIcon = styled(PencilLine)`
  ${OutlinedIcon}
`

const CalendarIcon = styled(CalendarRange)`
  ${FilledIcon}
`

const ChartLineIcon = styled(ChartLine)`
  ${OutlinedIcon}
`

const ClipboardIcon = styled(ClipboardList)`
  ${OutlinedIcon}
`

const FactoryIcon = styled(Factory)`
  ${OutlinedIcon}
`

const LotoReportsIcon = styled(LockKeyhole)`
  ${OutlinedIcon}
`

const ModelOperationsIcon = styled(Wrench)`
  ${OutlinedIcon}
`

const PieChartIcon = styled(ChartPie)`
  ${OutlinedIcon}
`

const BarChartIcon = styled(ChartGantt)`
  ${OutlinedIcon}
`

const DashboardIcon = styled(ChartGantt)`
  ${OutlinedIcon}
`

const PartsIcon = styled(Blocks)`
  ${OutlinedIcon}
`

const NetworkIcon = styled(Network)`
  ${OutlinedIcon}
`

const ProcessFlowIcon = styled(GitBranch)`
  ${OutlinedIcon}
`

const AssetsIcon = styled(Layers)`
  ${OutlinedIcon}
`

const DocIcon = styled(FileText)`
  ${OutlinedIcon}
`

const CasesIcon = styled(Notebook)`
  ${OutlinedIcon}
`

const LogbookIcon = styled(NotebookPen)`
  ${OutlinedIcon}
`

const ReportingIcon = styled(FileText)`
  ${OutlinedIcon}
`

const BudgetingIcon = styled(DollarSign)`
  ${OutlinedIcon}
`

const ProcessDataIcon = styled(ChartLine)`
  ${OutlinedIcon}
`
const ShipsIcon = styled(Ship)`
  ${OutlinedIcon}
`

const AnomalyDetectionIcon = styled(AlertTriangle)`
  ${OutlinedIcon}
`

const AlertsIcon = styled(Bell)`
  ${OutlinedIcon}
`

const NotificationsIcon = styled(BellRing)`
  ${OutlinedIcon}
`

const WorkflowIcon = styled(Workflow)`
  ${OutlinedIcon}
`

const DatabaseIcon = styled(Database)`
  ${OutlinedIcon}
`

const MetricsIcon = styled(BarChart3)`
  ${OutlinedIcon}
`

const UploadIcon = styled(UploadCloud)`
  ${OutlinedIcon}
`

const LONGRIDGE_SITE_ID = 'longridge-ac0a01'

export interface NavItemConfig {
  to: FileRouteTypes['to']
  params?: (siteID: string, orgID?: string) => Record<string, string>
  search?: (
    siteID: string,
    orgID: string | undefined
  ) => Record<string, string | undefined>
  icon: React.ComponentType
  label: MessageDescriptor
  filledIcon?: boolean
  permissions?: RolodexPermissionsValues[]
  except?: RolodexPermissionsValues[]
  shouldShow?: (context: {
    siteID: string
    canVisitSiteView: boolean
  }) => boolean
  /** Hide opt-in destinations until an org or site navbar config enables them. */
  requiresExplicitEnablement?: boolean
  /** Opt-in destination that is enabled by another item's navbar config entry. */
  enabledBy?: SelectedTab
}

export const NAV_ITEMS_CONFIG: Partial<Record<SelectedTab, NavItemConfig>> = {
  Home: {
    to: '/home',
    icon: HomeIcon,
    label: msg`Home`,
    filledIcon: false,
    permissions: [
      PERMISSION_GECKO_INTERNAL_MEMBER,
      PERMISSION_CANTILEVER_SINGAPORE_MEMBER,
    ],
  },
  Overview: {
    to: '/overview',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: MapIcon,
    label: msg`Site Overview`,
    filledIcon: false,
    shouldShow: ({ canVisitSiteView }) => canVisitSiteView,
  },
  'Production Dashboard': {
    to: '/production-dashboard',
    search: (siteID, orgID) =>
      createProductionDashboardSearch({
        orgId: orgID ?? '',
        siteId: siteID ?? '',
      }),
    icon: DashboardIcon,
    label: msg`Production Dashboard`,
    filledIcon: false,
  },
  Inbox: {
    to: '/inspection-dashboard/$orgOrSiteID',
    params: (siteID, orgID) => ({ orgOrSiteID: siteID ?? orgID ?? '' }),
    icon: ClipboardIcon,
    label: msg`Inspection Dashboard`,
    filledIcon: false,
  },
  Documents: {
    to: '/documents',
    params: (siteID) => ({ siteID }),
    icon: DocIcon,
    label: msg`Documents`,
    filledIcon: false,
    permissions: DOCUMENT_NAVIGATOR_VIEW_PERMISSIONS,
  },
  [SelectedTab.LotoReports]: {
    to: '/loto-reports',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: LotoReportsIcon,
    label: msg`LOTO reports`,
    filledIcon: false,
    permissions: [PERMISSION_CANTILEVER_LOTO_VIEWER],
    shouldShow: ({ siteID }) => Boolean(siteID),
  },
  'FIM Metrics': {
    to: '/fimmetrics',
    icon: MetricsIcon,
    label: msg`FIM Metrics`,
    filledIcon: false,
    permissions: DOCUMENT_NAVIGATOR_VIEW_PERMISSIONS,
  },
  Cases: {
    to: '/cases',
    icon: CasesIcon,
    label: msg`Cases`,
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_CASES_EDITOR,
      PERMISSION_CANTILEVER_CASES_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  Alerts: {
    to: '/alerts',
    icon: AlertsIcon,
    label: msg`Alerts`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  Notifications: {
    to: '/notifications',
    icon: NotificationsIcon,
    label: msg`Notifications`,
    filledIcon: false,
  },
  Assets: {
    to: '/assets/{-$siteID}',
    params: (siteID) => ({ siteID }),
    icon: AssetsIcon,
    label: msg`Assets`,
    permissions: ASSET_OVERVIEW_VIEW_PERMISSIONS,
  },
  Ships: {
    to: '/ships',
    search: (siteID, orgID) =>
      createShipsSearch({
        orgId: orgID,
        siteId: siteID || undefined,
      }),
    icon: ShipsIcon,
    label: msg`Ships`,
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_NAVY_SHIPS_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  Inspections: {
    to: '/inspections',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: InspectionsIcon,
    label: msg`Inspections`,
  },
  'Entry Browser': {
    to: '/entry-browser',
    icon: DatabaseIcon,
    label: msg`Entry Browser`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  Pipelines: {
    to: '/pipelines',
    icon: WorkflowIcon,
    label: msg`Pipelines`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Pipeline Fleet': {
    to: '/pipeline-fleet',
    icon: NetworkIcon,
    label: msg`Pipeline Fleet`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Fleet Analysis': {
    to: '/fleet-analysis',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: ChartLineIcon,
    label: msg`Fleet Analysis`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Inspection Scheduling': {
    to: '/scheduling',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: CalendarIcon,
    label: msg`Inspection Scheduling`,
    permissions: [
      PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_EDITOR,
      PERMISSION_CANTILEVER_INSPECTION_SCHEDULING_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Availability Planning': {
    to: '/availability-planning',
    icon: CalendarIcon,
    label: msg`Availability Planning`,
    permissions: [US_NAVY_ORG_PERMISSION, PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Health Plans': {
    to: '/health-plans',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: HeartIcon,
    label: msg`Health Plans`,
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_HEALTH_PLAN_VIEWER,
      PERMISSION_CANTILEVER_HEALTH_PLAN_EDITOR,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Budgeting: {
    to: '/budgeting',
    icon: BudgetingIcon,
    label: msg`Budgeting`,
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_BUDGETING_EDITOR,
      PERMISSION_CANTILEVER_BUDGETING_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  Walkdowns: {
    to: '/walkdowns',
    search: (siteId) => createWalkdownsSearch(siteId),
    icon: ClipboardIcon,
    label: msg`Walkdowns`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
    except: [US_NAVY_ORG_PERMISSION],
  },
  Reporting: {
    to: '/reporting',
    icon: ReportingIcon,
    label: msg`Reporting`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Forgings-reporting': {
    to: '/forgings-reporting',
    icon: ReportingIcon,
    label: msg`Forgings Reporting`,
    search: (siteId, orgId) => ({
      [SEARCH_PARAM_ORG_ID]: orgId,
      [SEARCH_PARAM_SITE_ID]: siteId,
    }),
    requiresExplicitEnablement: true,
  },
  'System Lens': {
    to: '/system-lens',
    search: (siteId, orgId) => ({
      [SEARCH_PARAM_ORG_ID]: orgId,
      [SEARCH_PARAM_SITE_ID]: siteId,
    }),
    icon: NetworkIcon,
    label: msg`System Lens`,
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_SYSTEM_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
  },
  'Point Cloud': {
    to: '/point-cloud',
    search: (siteId, orgId) =>
      createPointCloudIndexSearch({
        orgId: orgId,
        siteId: siteId,
      }),
    icon: MapIcon,
    label: msg`3D View`,
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_SYSTEM_VIEWER,
      PERMISSION_GECKO_INTERNAL_MEMBER,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Weld Map': {
    to: '/weldmap',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: PencilIcon,
    label: msg`Weld Map`,
    filledIcon: false,
  },
  Explorer: {
    to: '/explorer/$orgOrSiteID',
    params: (siteID, orgID) => ({ orgOrSiteID: siteID ?? orgID ?? '' }),
    icon: PieChartIcon,
    label: msg`Explorer`,
    filledIcon: false,
  },
  [SelectedTab.NnppOrders]: {
    to: '/nnpp/orders',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID,
    }),
    icon: ClipboardList,
    label: msg`Orders`,
    filledIcon: false,
    requiresExplicitEnablement: true,
  },
  [SelectedTab.NnppComponents]: {
    to: '/nnpp/components',
    icon: PartsIcon,
    label: msg`Components`,
    filledIcon: false,
    requiresExplicitEnablement: true,
    enabledBy: SelectedTab.NnppImports,
  },
  'Parts Thread': {
    to: '/parts',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: PartsIcon,
    label: msg`Parts Thread`,
    filledIcon: false,
    permissions: PARTS_DATABASE_PERMISSIONS_LIST,
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Process Flow Diagrams': {
    to: '/process-flows',
    search: (siteID, orgID) => {
      if (!siteID) {
        return {
          [SEARCH_PARAM_ORG_ID]: orgID,
          [SEARCH_PARAM_SITE_ID]: undefined,
        }
      }

      return createProcessFlowsSearch({
        orgId: orgID,
        siteId: siteID,
      })
    },
    icon: ProcessFlowIcon,
    label: msg`Process Flow Diagrams`,
    filledIcon: false,
    permissions: [PERMISSION_CANTILEVER_PROCESS_FLOW_ADMIN],
  },
  'Value Stories': {
    to: '/value-stories',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID,
    }),
    icon: DocIcon,
    label: msg`Value Stories`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  'Jobs Overview': {
    to: '/jobs',
    icon: BarChartIcon,
    label: msg`Jobs Overview`,
    filledIcon: false,
    permissions: [
      PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_ADMIN,
      PERMISSION_CANTILEVER_INSPECTION_OVERVIEW_EDITOR,
    ],
    except: [US_NAVY_ORG_PERMISSION],
  },
  'Process Data': {
    to: '/process-data',
    search: (siteId, orgId) =>
      createProcessDataOrgSiteSearch({
        orgId: orgId,
        siteId: siteId,
      }),
    icon: ProcessDataIcon,
    label: msg`Process Data`,
    filledIcon: false,
    permissions: [PERMISSION_CANTILEVER_TIME_SERIES_EDITOR],
  },
  'Anomaly Detection': {
    to: '/anomaly-detection',
    icon: AnomalyDetectionIcon,
    label: msg`Anomaly Detection`,
    filledIcon: false,
    permissions: [PERMISSION_CANTILEVER_ANOMALY_DETECTION_EDITOR],
  },
  Libra: {
    to: '/libra',
    search: (_siteID, orgID) =>
      createLibraSearch({
        orgId: orgID,
        workflowId: undefined,
        ticket: undefined,
        thread: undefined,
      }),
    icon: WorkflowIcon,
    label: msg`Libra`,
    filledIcon: false,
  },
  'NII Dashboard': {
    to: '/nii-dashboard',
    icon: DashboardIcon,
    label: msg`NII Dashboard`,
    filledIcon: false,
  },
  'Virtual Reports': {
    to: '/virtual-reports',
    search: (siteId, orgId) =>
      createVirtualReportsSearch({
        orgId,
        siteId: siteId || undefined,
      }),
    icon: DocIcon,
    label: msg`Virtual Reports`,
    filledIcon: false,
  },
  'Site Context & Monitoring': {
    to: '/site-context-monitoring',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: ProcessFlowIcon,
    label: msg`Site Context & Monitoring`,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
    shouldShow: ({ siteID }) => siteID === LONGRIDGE_SITE_ID,
  },
  FacilityModel: {
    to: '/facility-model',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: FactoryIcon,
    label: msg`Facility Model`,
    permissions: [PERMISSION_CANTILEVER_POWER_FIM_VIEWER],
  },
  ModelOperations: {
    to: '/model-operations',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: ModelOperationsIcon,
    label: msg`Model Operations`,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  Logbook: {
    to: '/logbook',
    search: (siteID) => ({
      [SEARCH_PARAM_SITE_ID]: siteID || undefined,
    }),
    icon: LogbookIcon,
    label: msg`Logbook`,
    filledIcon: false,
    permissions: [PERMISSION_GECKO_INTERNAL_MEMBER],
  },
  [SelectedTab.NnppReports]: {
    to: '/nnpp/reports',
    search: (siteID, orgID) => ({
      [SEARCH_PARAM_ORG_ID]: orgID,
      [SEARCH_PARAM_SITE_ID]: siteID,
    }),
    icon: DocIcon,
    label: msg`Reports`,
    filledIcon: false,
    requiresExplicitEnablement: true,
  },
  [SelectedTab.NnppImports]: {
    to: '/nnpp/imports',
    icon: UploadIcon,
    label: msg`Imports`,
    filledIcon: false,
    requiresExplicitEnablement: true,
  },
}
